import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { MapContainer, TileLayer, CircleMarker, Popup } from 'react-leaflet';
import { GeoJsonResponse, GeoJsonFeature } from '../../types/api';
import 'leaflet/dist/leaflet.css';

interface EventMapProps {
  data?: GeoJsonResponse;
  loading?: boolean;
}

export const EventMap: React.FC<EventMapProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data || !data.features || data.features.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography color="text.secondary">No geographic data available</Typography>
      </Box>
    );
  }

  // Calculate intensity for color coding (red to yellow based on event count)
  const maxEvents = Math.max(...data.features.map((f) => f.properties.eventCount));

  const getColor = (count: number): string => {
    const intensity = count / maxEvents;
    if (intensity > 0.7) return '#dc004e'; // High - red
    if (intensity > 0.4) return '#ff9800'; // Medium - orange
    return '#1976d2'; // Low - blue
  };

  const getRadius = (count: number): number => {
    const intensity = count / maxEvents;
    return Math.max(5, Math.min(20, 5 + intensity * 15));
  };

  return (
    <Box sx={{ height: 400, width: '100%', position: 'relative' }}>
      <MapContainer
        center={[20, 0]}
        zoom={2}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {data.features.map((feature: GeoJsonFeature, index: number) => {
          const [lng, lat] = feature.geometry.coordinates;
          const { eventCount, ipAddress, country, city } = feature.properties;

          return (
            <CircleMarker
              key={index}
              center={[lat, lng]}
              radius={getRadius(eventCount)}
              fillColor={getColor(eventCount)}
              color="#fff"
              weight={1}
              opacity={0.8}
              fillOpacity={0.6}
            >
              <Popup>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {ipAddress}
                  </Typography>
                  <Typography variant="body2">
                    Location: {city || 'Unknown'}, {country || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    Events: {eventCount.toLocaleString()}
                  </Typography>
                </Box>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>
    </Box>
  );
};
